import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { useCart } from "../context/CartContext";
import { imgUrl } from "../admin/adminApi";
import { discounted } from "./ServicesSection";
import Carousel from "./Carousel";
import { Plus, Check, Clock, Tag, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServiceCard = ({ s, type }) => {
  const { addToCart, items } = useCart();
  const inCart = items.find((i) => i.name === s.name);
  const dp = type === "service" ? discounted(s) : null;
  const pct = type === "package" && s.original_price ? Math.round((1 - s.price / s.original_price) * 100) : 0;
  return (
    <div className="svc-card bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl ring-1 ring-black/5 flex flex-col">
      <div className="img-zoom-wrap relative h-48 overflow-hidden">
        <img src={imgUrl(s.image)} alt={s.name} loading="lazy" className="img-zoom w-full h-full object-cover" />
        <span className="absolute top-3 left-3 bg-white/95 text-primary text-xs font-bold px-3 py-1 rounded-full shadow">
          {dp != null ? <>₹{dp} <span className="line-through text-gray-400 ml-1">₹{s.price}</span></> : <>₹{s.price}{type === "package" && s.original_price ? <span className="line-through text-gray-400 ml-1">₹{s.original_price}</span> : null}</>}
        </span>
        {dp != null && <span className="absolute top-3 right-3 bg-accent text-white text-xs font-bold px-2.5 py-1 rounded-full shadow">{s.offer_type === "percent" ? `${s.offer_value}% OFF` : `₹${s.offer_value} OFF`}</span>}
        {pct > 0 && <span className="absolute top-3 right-3 bg-accent text-white text-xs font-bold px-2.5 py-1 rounded-full shadow">{pct}% OFF</span>}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display text-xl font-bold text-gray-900">{s.name}</h3>
        <p className="text-sm text-gray-600 mt-2 flex-1">{s.description}</p>
        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">{s.duration && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {s.duration}</span>}{s.category && <span>{s.category}</span>}</div>
        <Button onClick={() => addToCart({ name: s.name, price: dp != null ? dp : s.price, type })} disabled={!!inCart} className="mt-4 bg-primary hover:bg-primary/90 rounded-full h-10 font-semibold">
          {inCart ? <><Check className="w-4 h-4 mr-1" /> Added</> : <><Plus className="w-4 h-4 mr-1" /> Add to Cart</>}
        </Button>
      </div>
    </div>
  );
};

const PopularServices = ({ selectedCategory = "All", setSelectedCategory }) => {
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [offers, setOffers] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data)).catch(() => {});
    axios.get(`${API}/packages`).then((r) => setPackages(r.data)).catch(() => {});
    axios.get(`${API}/offers`).then((r) => setOffers(r.data)).catch(() => {});
    axios.get(`${API}/categories`).then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const chips = ["All", ...categories.map((c) => c.name)];
  const shown = selectedCategory === "All" ? services : services.filter((s) => s.category === selectedCategory);

  return (
    <section id="services" className="py-14 md:py-20 bg-secondary/50 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12">
        {offers.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-4 mb-12">
            {offers.map((o) => (
              <div key={o.id} className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-5 text-white flex items-center justify-between">
                <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10" />
                <div className="relative"><div className="flex items-center gap-2 text-white/90 text-sm"><Tag className="w-4 h-4 text-accent" /> Limited Offer</div><div className="font-display text-xl font-bold mt-1">{o.title}</div><p className="text-white/80 text-sm mt-0.5">{o.description}</p></div>
                <div className="relative text-center"><div className="text-3xl font-extrabold">{o.discount_value}{o.discount_type === "percent" ? "%" : "₹"}</div><div className="font-mono bg-white/20 rounded px-2 py-0.5 text-xs mt-1">{o.code}</div></div>
              </div>
            ))}
          </div>
        )}

        <div className="text-center mb-8">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900">Pick Your Favourite Services</h2>
          <p className="text-gray-600 mt-3 max-w-2xl mx-auto">Add multiple services & packages to your cart and book in one go — across Gurugram.</p>
        </div>

        {/* category filter chips */}
        <div className="flex flex-wrap justify-center gap-2 mb-10 no-scrollbar">
          {chips.map((c) => (
            <button key={c} onClick={() => setSelectedCategory?.(c)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${selectedCategory === c ? "bg-primary text-primary-foreground shadow" : "bg-white text-gray-700 ring-1 ring-black/5 hover:ring-primary/30"}`}>
              {c}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {shown.map((s) => <ServiceCard key={s.id} s={s} type="service" />)}
        </div>
        {shown.length === 0 && <p className="text-center text-gray-500 py-10">No services in this category yet.</p>}

        {packages.length > 0 && selectedCategory === "All" && (
          <div className="mt-16">
            <div className="text-center mb-8"><span className="inline-flex items-center gap-2 text-primary font-semibold"><Sparkles className="w-4 h-4 text-accent" /> VALUE PACKAGES</span><h3 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mt-2">Save More with Combo Packages</h3></div>
            <Carousel>
              {packages.map((p) => (
                <div key={p.id} className="w-[300px] shrink-0 snap-start">
                  <ServiceCard s={p} type="package" />
                </div>
              ))}
            </Carousel>
          </div>
        )}
      </div>
    </section>
  );
};

export default PopularServices;
